import React, {createContext, useContext, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useCustomerType} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useHydrated} from '../hooks/use-hydrated'
import {useOptimizelyDecisions} from '../hooks/use-optimizely-decisions'
import {useOptimizelyFxConfig} from '../hooks/use-optimizely-fx-config'
import {abKey, groupsKey} from '../utils/audience-storage-keys'
import {sanitizeToken} from '../utils/gdi-audience-markers'
import {normalizeCustomerGroups, normalizeLoyaltyTier} from '../utils/optimizely-attributes'

/* `renderAll` = match the SSR HTML, which carries every variant. Also the
 * default outside a provider (tests), where nothing is filtered. */
const RENDER_ALL = Object.freeze({renderAll: true})
const AudienceContext = createContext(RENDER_ALL)

const writeKey = (key, value) => {
    try {
        if (value == null) window.localStorage.removeItem(key)
        else if (window.localStorage.getItem(key) !== value) window.localStorage.setItem(key, value)
    } catch {
        // storage blocked (private mode) — the pre-paint script degrades to guest
    }
}

/* Same encoding as the render markers, so the pre-paint selectors match. */
const toPrepaintDecisions = (decisions) =>
    Object.fromEntries(
        Object.entries(decisions || {})
            .map(([flag, variation]) => [sanitizeToken(flag), sanitizeToken(variation)])
            .filter(([flag, variation]) => flag && variation)
    )

/*
 * Computes the audience state ONCE for the whole tree (useOptimizelyPage has
 * ~30 instances per page) and persists what the pre-paint <head> script needs
 * on the NEXT page load. Each dimension resolves on its own: auth from
 * localStorage right after mount, groups when the customer query settles
 * (guests resolve to none), A/B when the decisions call returns.
 */
export const AudienceProvider = ({children}) => {
    const hydrated = useHydrated()
    const {isRegistered} = useCustomerType()
    const customerQuery = useCurrentCustomer()
    const {siteId} = useOptimizelyFxConfig()

    const isLoggedIn = hydrated && !!isRegistered
    const groupsCsv = normalizeCustomerGroups(customerQuery.data?.c_customerGroups).join(',')
    const loyaltyTier = isLoggedIn
        ? normalizeLoyaltyTier(customerQuery.data?.c_loyaltyMostRecentTier)
        : ''
    // A disabled customer query (fetchStatus 'idle') will never settle — treat as resolved.
    const groupsSettled =
        !isLoggedIn ||
        customerQuery.isSuccess ||
        customerQuery.isError ||
        customerQuery.fetchStatus === 'idle'
    const ab = useOptimizelyDecisions({
        hydrated,
        isLoggedIn,
        customerGroupsCsv: groupsCsv,
        loyaltyTier,
        settled: groupsSettled
    })

    const audience = useMemo(() => {
        if (!hydrated) return RENDER_ALL
        return {
            isLoggedIn,
            customerGroups: isLoggedIn && groupsCsv ? groupsCsv.split(',') : [],
            decisions: ab.decisions,
            knownFlagKeys: ab.knownFlagKeys,
            decisionsPathname: ab.pathname,
            status: {auth: 'resolved', group: groupsSettled ? 'resolved' : 'pending', ab: ab.status}
        }
    }, [hydrated, isLoggedIn, groupsCsv, groupsSettled, ab])

    useEffect(() => {
        if (!siteId || audience.renderAll) return
        if (!audience.isLoggedIn) {
            writeKey(groupsKey(siteId), null)
        } else if (audience.status.group === 'resolved') {
            const tokens = audience.customerGroups.map(sanitizeToken).filter(Boolean).sort()
            writeKey(groupsKey(siteId), tokens.length ? tokens.join(' ') : null)
        }
    }, [siteId, audience])

    useEffect(() => {
        if (!siteId || audience.renderAll || audience.status.ab === 'pending') return
        if (audience.status.ab === 'resolved' && !audience.decisionsPathname) return
        const payload =
            audience.status.ab === 'resolved'
                ? {
                      enabled: true,
                      degraded: false,
                      pathname: audience.decisionsPathname,
                      decisions: toPrepaintDecisions(audience.decisions)
                  }
                : {enabled: false, degraded: false, decisions: {}}
        writeKey(abKey(siteId), JSON.stringify(payload))
    }, [siteId, audience])

    return <AudienceContext.Provider value={audience}>{children}</AudienceContext.Provider>
}

AudienceProvider.propTypes = {
    children: PropTypes.node
}

/** The audience state for filterAudienceTargeting; `renderAll` until mounted. */
export const useAudienceState = () => useContext(AudienceContext)
